import { useCallback } from 'react';

import { StreamType } from '@smart-tv/typings/content';
import { TrickModes } from '@smart-tv/components/player/types';

import { usePlayerContext } from '../../_contexts/player-context';
import useStream from '../../_hooks/use-stream';
import { Props } from './player-controls-navigation';
import { useSetAtom, atom } from 'jotai';
import useDeactivatePlayerActions from '../_hooks/use-deactivate-player-actions';
import { descalePlayerAtom, openChannelsAtom } from '../../atoms';

export const playerBtnsFocusHistoryAtom = atom<string>('');

const usePlayerControlsNavigation = (props: Props) => {
  const { controlsOpen, closeControls } = props;
  const { stream, streamType } = useStream();
  const { toggleSettingsOverlay } = usePlayerContext();
  const isDeactivated = useDeactivatePlayerActions();

  const setPlayerBtnsFocusHistory = useSetAtom(playerBtnsFocusHistoryAtom);
  const setDescalePlayer = useSetAtom(descalePlayerAtom);
  const setOpenChannels = useSetAtom(openChannelsAtom);

  const handleOpenProgramInfo = useCallback(() => {
    if (!isDeactivated) {
      setDescalePlayer(true);
      closeControls();
    }
  }, [isDeactivated, setDescalePlayer, closeControls]);

  /*
  const handleOpenChannelList = useCallback(() => {
    if (!isDeactivated) {
      closeControls();
      setOpenChannels(true);
    }
  }, [isDeactivated, closeControls, setOpenChannels]);
  */

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement == null) {
      document.documentElement.requestFullscreen().catch(() => undefined);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => undefined);
      }
    }
  }, []);
  const handleOpenSettingsOverlay = useCallback(() => {
    if (controlsOpen && !isDeactivated) {
      toggleSettingsOverlay();
      closeControls();
    }
  }, [controlsOpen, closeControls, toggleSettingsOverlay, isDeactivated]);

  const enableStartOverButton = useCallback(() => {
    if (!stream?.trickmodes || streamType === StreamType.CatchUp) {
      return false;
    }

    return (stream?.trickmodes & TrickModes.StartoverAllow) !== 0;
  }, [stream?.trickmodes, streamType]);

  const enableForwardButton = useCallback(() => {
    return Boolean(stream?.trickmodes) && streamType !== StreamType.LiveStream;
  }, [stream?.trickmodes, streamType]);

  const enableRewindButton = useCallback(() => {
    if (!stream?.trickmodes || streamType === StreamType.LiveStream) return;

    if (streamType === StreamType.StartOver) {
      return allowTrickmodes(TrickModes.StartoverAllowRewind, stream.trickmodes);
    }
    if (streamType === StreamType.CatchUp || streamType === StreamType.Vod) {
      return allowTrickmodes(TrickModes.CatchupAllowRewind, stream.trickmodes);
    }
  }, [stream?.trickmodes, streamType]);

  return {
    handleOpenProgramInfo,
    // handleOpenChannelList,
    handleOpenSettingsOverlay,
    enableStartOverButton,
    enableForwardButton,
    enableRewindButton,
    setPlayerBtnsFocusHistory,
    isLiveStream: streamType === StreamType.LiveStream,
    toggleFullscreen,
    isDeactivated
  };
};

export const allowTrickmodes = (
  trickmodesType: TrickModes,
  trickmodes: number,
  fastforwardLimit = 0,
  playerPosition = 0
) => {
  if (!trickmodes) return false;
  if (StreamType.CatchUp && fastforwardLimit - playerPosition > 30) {
    return true;
  }
  // the bitwise AND (&) is used to check if specific trick modes are allowed.
  // For instance, stream?.trickmodes & TrickModes.StartoverAllow checks if the StartoverAllow bit is set in stream?.trickmodes.
  return (trickmodes & trickmodesType) !== 0;
};

export default usePlayerControlsNavigation;
